/**
 * Статус-пігулка — єдиний вигляд стану документа в усій системі.
 *
 * Палітра: рівно шість тонів (чотири семантичні + коньячний + нейтральний),
 * взяті з `handoff/tokens.css`. Нових не додавати — якщо статусу немає тону,
 * обери найближчий за змістом.
 */
import type { ReactNode } from 'react';

export type ChipTone = 'brand' | 'success' | 'warning' | 'error' | 'info' | 'neutral';

const TONES: Record<ChipTone, { color: string; bg: string }> = {
  brand: { color: '#8B3A0F', bg: '#F7EBDD' },
  success: { color: '#2E6B3E', bg: '#EAF2EA' },
  warning: { color: '#B45309', bg: '#FDF0D9' },
  error: { color: '#B42318', bg: '#FEF3F2' },
  info: { color: '#175CD3', bg: '#EAF1FE' },
  neutral: { color: '#8A8272', bg: '#F1EFE9' },
};

const SIZES = {
  md: { fontSize: 11.5, padding: '3px 10px' },
  sm: { fontSize: 10.5, padding: '2px 8px' },
};

export function StatusChip({
  tone,
  size = 'md',
  dot = true,
  children,
}: {
  tone: ChipTone;
  size?: keyof typeof SIZES;
  /** Крапка ліворуч. Прототип лишає її статусу документа і знімає з оплати. */
  dot?: boolean;
  children: ReactNode;
}) {
  const { color, bg } = TONES[tone];
  const dims = SIZES[size];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontSize: dims.fontSize,
        fontWeight: 600,
        lineHeight: 1.5,
        color,
        background: bg,
        borderRadius: 999,
        padding: dims.padding,
        whiteSpace: 'nowrap',
      }}
    >
      {dot && (
        <span
          style={{
            width: 5,
            height: 5,
            borderRadius: '50%',
            background: color,
            flexShrink: 0,
          }}
        />
      )}
      {children}
    </span>
  );
}
